import axios from 'axios'

import { envConfig } from '@twisto/base/env-config'

export type KeycloakTokenResponse = {
  access_token: string
  expires_in: number
  refresh_token: string
}

export type KeycloakTokenError = {
  error?: string
  error_description?: string
}

export type PasswordGrantCredentials = {
  password: string
  username: string
}

export type KeycloakAccessTokenClaims = {
  sub: string
  tenantId: string | null
}

export const AUTH_CLIENT_ID =
  process.env.NEXT_PUBLIC_AUTH_CLIENT_ID ??
  envConfig.AUTH?.CLIENT_ID ??
  'webapp'
export const AUTH_REALM = process.env.NEXT_PUBLIC_AUTH_REALM ?? 'company'
export const KEYCLOAK_BASE_URL =
  process.env.NEXT_PUBLIC_AUTH_ISSUER ??
  envConfig.AUTH?.ISSUER ??
  `${process.env.NEXT_PUBLIC_API_URL ?? envConfig.B2B_API_ENDPOINT}/realms/${AUTH_REALM}`
export const KEYCLOAK_TOKEN_PATH = '/protocol/openid-connect/token'

const keycloakClient = axios.create({
  baseURL: KEYCLOAK_BASE_URL,
})

const tokenGrant = async (form: URLSearchParams) => {
  const { data } = await keycloakClient.post<KeycloakTokenResponse>(
    KEYCLOAK_TOKEN_PATH,
    form,
    {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    }
  )

  return data
}

export const passwordGrant = ({
  password,
  username,
}: PasswordGrantCredentials) =>
  tokenGrant(
    new URLSearchParams({
      client_id: AUTH_CLIENT_ID,
      grant_type: 'password',
      password,
      username,
    })
  )

export const refreshGrant = (refreshToken: string) =>
  tokenGrant(
    new URLSearchParams({
      client_id: AUTH_CLIENT_ID,
      grant_type: 'refresh_token',
      refresh_token: refreshToken,
    })
  )

export const readKeycloakAccessTokenClaims = (
  token: string
): KeycloakAccessTokenClaims | null => {
  try {
    const payload = token.split('.')[1]

    if (!payload) return null

    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      '='
    )
    const claims = JSON.parse(atob(padded)) as {
      sub?: unknown
      tenant_id?: unknown
    }

    if (typeof claims.sub !== 'string' || claims.sub.length === 0) {
      return null
    }

    return {
      sub: claims.sub,
      tenantId:
        typeof claims.tenant_id === 'string' && claims.tenant_id.length > 0
          ? claims.tenant_id
          : null,
    }
  } catch {
    return null
  }
}
